// Der Stand des Argus-Moduls (internal/argus). Er kommt über das
// Homelab-Protokoll in Teilen (module/argus/protokoll.ts,
// docs/protokoll.md › „Argus über das Protokoll“): jedes Feld der obersten
// Ebene von GET /v2-api/argus und jeder Lauf ist eine Instanz, nur was sich
// ändert, geht neu. Schattenbetrieb und Kennzeichen (zusammen ~37 kB, neu bei
// jedem Abruf der Zentrale) und die vorgemerkten Beispiele (nachholen, ~20 kB)
// kommen nur, solange eine Seite sie zeigt (`argusGross`). Kein REST-Abruf mehr (bis 03.11.2026 holte jeder Tab beim
// Start ~86 kB zusätzlich zum Snapshot): bis der Snapshot da ist, zeigt die
// Ablage den letzten Stand, nach einer Unterbrechung holt das Protokoll selbst
// nach. Nur ob das Modul aus ist, fragt die erste Seite, die es wissen will
// (`argusAus`). Das Backend liest die Ablage, die Seiten lesen nur diesen Store
// (docs/data-loading.md).
//
// Läuft seit dem Boot (App.svelte): Baum, Marke an der Leiste und „läuft
// gerade" brauchen den Stand auf jeder Seite.

import { SvelteMap } from "svelte/reactivity";

import { getJSON, postJSON } from "../../lib/api";
import { loadPersisted, persist } from "../../lib/persistent-summary.svelte";
import { abonniere } from "../../lib/protokoll/client.svelte";
import { instanzOrt, liesJson, ortSchluessel, setzeFeld, standAus, teileAus, type Teile } from "./protokoll";
import type { Box, Kasten, Lauf, PruefDaten, Stand, Werte } from "./typen";

const STORAGE_KEY = "argus";

const _stand = new SvelteMap<string, Stand>();
let _aus = $state(false);
let _ausGefragt = false;
let _started = false;

// Die Teile, wie sie zuletzt kamen; dazu je Teil der JSON-Text (ein
// Lebenszeichen mit demselben Text baut nichts neu).
const _teile: Teile = { felder: new Map(), laeufe: new Map() };
const _roh = new Map<string, string>();
let _baut = false;

function uebernimm(s: Stand): void {
  if (!s || typeof s.bereit !== "boolean") return;
  _stand.set("current", s);
  persist(STORAGE_KEY, s);
}

// Baut den Stand einmal je Runde: ein Abo bringt ~30 Instanzen auf einmal.
function baueBald(): void {
  if (_baut) return;
  _baut = true;
  queueMicrotask(() => {
    _baut = false;
    const s = standAus(_teile, Date.now());
    if (s) uebernimm(s);
  });
}

function beiStand(inst: string, daten: unknown): void {
  const ort = instanzOrt(inst);
  const j = ort && liesJson(daten);
  if (!ort || !j) return;
  const k = ortSchluessel(ort);
  if (_roh.get(k) === j.roh) return;
  _roh.set(k, j.roh);
  if (ort.art === "lauf") _teile.laeufe.set(ort.lauf, j.wert as Lauf);
  else setzeFeld(_teile, ort.feld, j.wert);
  baueBald();
}

function beiWeg(inst: string): void {
  const ort = instanzOrt(inst);
  if (!ort) return;
  const k = ortSchluessel(ort);
  _roh.delete(k);
  if (ort.art === "lauf") _teile.laeufe.delete(ort.lauf);
  else _teile.felder.delete(ort.feld);
  baueBald();
}

export function startArgus(): void {
  if (_started) return;
  _started = true;
  const cached = loadPersisted<Stand>(STORAGE_KEY);
  if (cached && "bereit" in cached) {
    _stand.set("current", cached);
    const t = teileAus(cached);
    for (const [k, v] of t.felder) _teile.felder.set(k, v);
    for (const [k, v] of t.laeufe) _teile.laeufe.set(k, v);
  }
  abonniere("argus.*.stand", { rate: "normal" }, { stand: beiStand, weg: beiWeg });
  abonniere("argus.lauf.*.stand", { rate: "normal" }, { stand: beiStand, weg: beiWeg });
}

/** Schattenbetrieb, Kennzeichen und vorgemerkte Beispiele (argus.*.gross) lesen, solange die Seite
 *  sie zeigt: `$effect(() => untrack(argusGross))` -- untrack, sonst hinge
 *  der Effekt an den Ständen, die abonniere liest, und bestellte bei jedem
 *  Delta ab und neu. Gibt die Abmeldung zurück. */
export function argusGross(): () => void {
  return abonniere("argus.*.gross", { rate: "normal" }, { stand: beiStand, weg: beiWeg });
}

/** Der Stand; undefined, solange noch nichts da ist. */
export function getArgus(): Stand | undefined {
  return _stand.get("current");
}

/** Ist das Modul ausgeschaltet (HOMELAB_ARGUS=aus: keine Routen, keine
 *  Topics)? Fragt beim ersten Aufruf GET /v2-api/argus?nur=an (404 = aus);
 *  bis dahin false. */
export function argusAus(): boolean {
  if (!_ausGefragt) {
    _ausGefragt = true;
    void fetch("/v2-api/argus?nur=an", { credentials: "include" })
      .then((r) => {
        _aus = r.status === 404;
      })
      .catch(() => {
        // Neustart oder Netz weg: die nächste Seite fragt neu.
        _ausGefragt = false;
      });
  }
  return _aus;
}

// ── Protokoll eines Laufs ────────────────────────────────────────────
// Geholt, wenn eine Seite es zeigt, und neu, sobald der Lauf weiter
// geschrieben hat (log_bytes im Stand). Aus $effect oder einem Klick
// aufrufen, nie aus $derived.

const _protokoll = new SvelteMap<string, { zeilen: string[]; bytes: number }>();
const _holt = new Set<string>();

/** art „hailo": hailo-log.txt (Ausgabe des DFC) statt log.txt; „sammeln":
 *  das Protokoll eines Sammellaufs (lauf = Stand). */
export function ladeProtokoll(lauf: string, bytes: number, art: "training" | "hailo" | "sammeln" = "training"): void {
  const k = `${art}:${lauf}`;
  const da = _protokoll.get(k);
  if (!lauf || _holt.has(k) || (da && da.bytes === bytes)) return;
  _holt.add(k);
  const url =
    art === "sammeln"
      ? `/v2-api/argus/sammeln/protokoll?stand=${encodeURIComponent(lauf)}&zeilen=300`
      : `/v2-api/argus/laeufe/${encodeURIComponent(lauf)}/protokoll?zeilen=300${art === "hailo" ? "&art=hailo" : ""}`;
  void getJSON<{ zeilen: string[] | null }>(url)
    .then((p) => _protokoll.set(k, { zeilen: p?.zeilen ?? [], bytes }))
    .catch(() => _protokoll.set(k, { zeilen: [], bytes }))
    .finally(() => _holt.delete(k));
}

export function getProtokoll(lauf: string, art: "training" | "hailo" | "sammeln" = "training"): string[] | undefined {
  return _protokoll.get(`${art}:${lauf}`)?.zeilen;
}

// ── Aufträge (hinter der Vault-PIN; die Eingabe zeigt lib/pin-stufe) ──

/** Beispiel des Schattens zum Prüfen vormerken (Vault-PIN, eigenes Tor):
 *  die nächste Nacht holt das ganze Bild aus der Aufnahme. */
export async function zumPruefen(id: string): Promise<void> {
  await postJSON("/v2-api/argus/schatten/nachholen", { id });
}

/** Ausrollen: auf worker1 bringen und in den Schatten (Vault-PIN). */
export async function ausrollen(lauf: string): Promise<void> {
  await postJSON("/v2-api/argus/ausrollen", { lauf });
}

/** Einstellungen speichern (Vault-PIN); liefert die wirksamen Werte. */
export async function werteSpeichern(w: Werte): Promise<Werte> {
  const ohne: Werte = { ...w };
  delete ohne.geaendert;
  return await postJSON<Werte>("/v2-api/argus/einstellungen", ohne);
}

export async function trainingStarten(wann: "jetzt" | "nacht", epochen: number): Promise<void> {
  await postJSON("/v2-api/argus/training/auftrag", { wann, epochen });
}

export async function planungAbsagen(): Promise<void> {
  await postJSON("/v2-api/argus/training/absagen");
}

export async function trainingBeenden(): Promise<void> {
  await postJSON("/v2-api/argus/training/beenden");
}

/** Für den Hailo umwandeln (oder neu): _training/hailo-auftrag.json; forge
 *  nimmt ihn binnen 5 min. Hinter der Vault-PIN wie Starten. */
export async function hailoBeauftragen(lauf: string, rechner: "homeserver2" | "forge"): Promise<void> {
  await postJSON("/v2-api/argus/hailo/auftrag", { lauf, rechner });
}

/** Schattenbetrieb schalten (an/aus, welches HEF): an die Argus-Zentrale,
 *  die es dem Detektor auf worker1 schickt. Hinter der Vault-PIN. */
export async function schattenSchalten(an: boolean, modell: string): Promise<void> {
  await postJSON("/v2-api/argus/schatten", { an, modell });
}

/** Beispielbild des Schattens (Vault-PIN wie „Bilder prüfen“; `frisch`
 *  nach einer neuen Freigabe). */
export function schattenBildUrl(id: string, frisch = 0): string {
  return `/v2-api/argus/schatten/beispiel/${encodeURIComponent(id)}${frisch ? `?f=${frisch}` : ""}`;
}

// ── Bilder prüfen ────────────────────────────────────────────────────
// Die Bilder eines Stands (bis ~1500, je mit Vorschlägen und Urteil)
// werden geholt, wenn die Prüfansicht den Stand zeigt; danach hält die
// Seite ihre eigene Arbeitskopie (Urteile ändern sie an Ort und Stelle,
// wie in Argus). Die Zähler im Baum kommen über den Stand.
//
// Alles hier steht hinter der Vault-PIN (Tim 28.10.2026), mit eigenem Tor
// wie der Vault-Browser: 423 ohne "stufe" -- die Seite zeigt dann ihre
// eigene Eingabe (VaultPinGate), nicht das allgemeine Fenster.

export type PruefLaden = PruefDaten | { fehler: string } | { gesperrt: true };

const _pruefen = new SvelteMap<string, PruefLaden>();

export function ladePruefBilder(stand: string, neu = false): void {
  if (!stand || (!neu && _pruefen.has(stand))) return;
  // neu: die alte Liste fällt weg, sonst nimmt die Seite sie für die frische
  // (Tim 04.11.2026: „Prüfen ist auf neun aber keine Bilder hier sichtbar“)
  if (neu) _pruefen.delete(stand);
  void fetch(`/v2-api/argus/pruefen/${encodeURIComponent(stand)}/bilder`, { credentials: "include", headers: { Accept: "application/json" } })
    .then(async (r) => {
      if (r.status === 423) {
        _pruefen.set(stand, { gesperrt: true });
        return;
      }
      if (!r.ok) {
        const d = (await r.json().catch(() => ({}))) as { detail?: string };
        _pruefen.set(stand, { fehler: d.detail ?? `HTTP ${r.status}` });
        return;
      }
      _pruefen.set(stand, (await r.json()) as PruefDaten);
    })
    .catch((e) => _pruefen.set(stand, { fehler: e instanceof Error ? e.message : String(e) }));
}

/** undefined = noch nicht geladen */
export function getPruefBilder(stand: string): PruefLaden | undefined {
  return _pruefen.get(stand);
}

/** Ist die Vault-PIN gerade frei? (GET /v2-api/vault/pin/status, schiebt
 *  nichts vor) -- wenn ein Bild nicht lädt: abgelaufen oder etwas anderes? */
export async function pinFrei(): Promise<boolean> {
  try {
    const s = await getJSON<{ configured: boolean; unlocked: boolean }>("/v2-api/vault/pin/status");
    return !s.configured || s.unlocked;
  } catch {
    return true;
  }
}

/** Adresse eines Bilds; `frisch` (> 0) nach einer neuen Freigabe, damit der
 *  Browser ein vorher gesperrtes Bild nicht aus seinem Fehlschlag nimmt;
 *  `version`: neu geholte Bilder (markiert, Schatten) haben eine neue
 *  Fassung unter derselben id -- der Browser hält Bilder einen Tag. */
export function bildUrl(stand: string, id: string, aus?: Box, frisch = 0, version = 0): string {
  const q: string[] = [];
  if (aus) q.push(`aus=${aus.map((v) => Math.min(1, Math.max(0, v)).toFixed(5)).join(",")}`);
  if (frisch) q.push(`f=${frisch}`);
  if (version) q.push(`v=${version}`);
  return `/v2-api/argus/pruefen/${encodeURIComponent(stand)}/bild/${id}.jpg${q.length ? "?" + q.join("&") : ""}`;
}

/** Fehler eines Urteils; `gesperrt`, wenn die PIN abgelaufen ist (423). */
export class PruefFehler extends Error {
  gesperrt: boolean;
  constructor(text: string, gesperrt: boolean) {
    super(text);
    this.gesperrt = gesperrt;
  }
}

async function schicke<T>(pfad: string, body: unknown): Promise<T> {
  try {
    return await postJSON<T>(pfad, body);
  } catch (e) {
    const status = (e as { status?: number }).status;
    throw new PruefFehler(status === 423 ? "Die Vault-PIN ist abgelaufen." : e instanceof Error ? ((e as { detail?: string }).detail ?? e.message) : String(e), status === 423);
  }
}

export async function urteilen(stand: string, id: string, status: "geprueft" | "verworfen" | "offen", kaesten: Kasten[], nachgeprueft = false): Promise<{ von: string }> {
  const r = await schicke<{ urteil: { von: string } }>("/v2-api/argus/pruefen/urteil", { stand, id, status, kaesten, nachgeprueft });
  return r.urteil;
}

export async function vorschlaegeEntfernen(stand: string, entfernt: { id: string; box: Box }[]): Promise<number> {
  const r = await schicke<{ entfernt: number }>("/v2-api/argus/pruefen/vorschlaege-entfernen", { stand, entfernt });
  return r.entfernt;
}
